import { gql } from "@apollo/client";
import { formatCurrency } from "@asmbl/shared/money";
import { formatNumeral } from "@asmbl/shared/utils";
import makeStyles from "../../../makeStylesV4Compat";
import {
  calculateEquityFromVestEvents,
  filterVestEventsForUnvested,
  VestEvent,
} from "../../../models/EquityVestEvent";
import { GRAY_4 } from "../../../theme";
import {
  TotalUnvestedEquityChartHeader_equityGrantVestEvent as EquityGrantVestEvent,
  TotalUnvestedEquityChartHeader_equityHoldings as EquityHoldings,
} from "../../../__generated__/graphql";
import { AssembleTypography } from "../../AssembleTypography";
import { useCurrencies } from "../../CurrenciesContext";
import { useTotalEquitySlider } from "../../TotalEquitySlider/TotalEquitySliderContext";

const useStyles = makeStyles(() => ({
  element: {
    display: "flex",
    flexDirection: "column",
  },
  header: { color: GRAY_4 },
  cash: { fontWeight: 500 },
  units: { letterSpacing: "-1px" },
}));

type Props = {
  equityGrantVestEvents: EquityGrantVestEvent[];
  equityHoldings: EquityHoldings;
};

export function TotalUnvestedEquityChartHeader({
  equityGrantVestEvents,
  equityHoldings,
}: Props): JSX.Element {
  const date = new Date();
  const classes = useStyles();
  const { currencies } = useCurrencies();
  const equitySliderData = useTotalEquitySlider();

  const sharePriceTarget = equitySliderData?.value;

  const hasVestingData = equityGrantVestEvents.length > 0;

  const unvestedVestEvents = filterVestEventsForUnvested(
    equityGrantVestEvents as VestEvent[],
    date
  );

  const unvestedEquity = calculateEquityFromVestEvents(
    currencies,
    sharePriceTarget,
    unvestedVestEvents,
    equityHoldings.valuationCurrency
  );

  return (
    <div className={classes.element}>
      <AssembleTypography
        className={classes.header}
        variant="productEyebrowMedium"
      >
        Unvested
      </AssembleTypography>

      {hasVestingData ? (
        <>
          <AssembleTypography
            className={classes.cash}
            variant="productLongForm"
            data-cy="unvested-equity-header-cash"
          >
            {unvestedEquity.value !== null
              ? formatCurrency(unvestedEquity.value, {
                  maximumFractionDigits: 0,
                })
              : "-"}
          </AssembleTypography>
          <AssembleTypography
            className={classes.units}
            variant="sidebarTitle"
            data-cy="unvested-equity-header-units"
          >
            {`${formatNumeral(unvestedEquity.count)} units`}
          </AssembleTypography>
        </>
      ) : (
        <AssembleTypography className={classes.cash} variant="productLongForm">
          No Data
        </AssembleTypography>
      )}
    </div>
  );
}

TotalUnvestedEquityChartHeader.fragments = {
  equityHoldings: gql`
    fragment TotalUnvestedEquityChartHeader_equityHoldings on EquityHoldings {
      id
      valuationCurrency {
        id
        code
        exchangeRate
      }
    }
  `,
  equityGrantVestEvent: gql`
    fragment TotalUnvestedEquityChartHeader_equityGrantVestEvent on EquityGrantVestEventSource {
      id
      vestingDate
      unitCount
      grant {
        id
        sharePrice
        unitCount
      }
    }
  `,
};
